import React, { useEffect, useMemo } from 'react';
import CompaniesCore from '../components/CompaniesCore';
import type { ITopChartsCompaniesPageProps } from '../types';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import moment from 'moment';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { handleCompiniesFilterParamsToApi } from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import useFilterOptions from '@intelligence/pages/TopCharts/Companies/components/CompaniesCore/hooks/useFilterOptions';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import { COMPANIES_DEFAULT_FILTER_VALUE } from '@intelligence/pages/TopCharts/Companies/components/CompaniesCore/constants';
import {
  getTopCompaniesCustomValidUrlParams,
  topchartCompaniesDownloadsValidCfg,
  hasShortLinkRatio,
} from '@intelligence/_config/urlPageValidCfg';
import { useShortLink } from '@intelligence/hooks/useShortLink';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

export default function Downloads(props: ITopChartsCompaniesPageProps) {
  const {} = props;

  const pageLogInfo = dbiPageLogMes?.topchartsCompanyDownloads;

  const { loading, regionOptions, headQuatersOptionsData } = useFilterOptions();
  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.companiesDownloads?.type,
  });

  // 是否有短链接更新功能
  const hasUrlShortLink = hasShortLinkRatio.topCompaniesDownloads;

  const {
    initShortLinkCfg,
    shortLinkLoading,
    urlCustomParams,
    shortLinkFilterValue,
    shortLinkValidQuery,
  } = useShortLink({
    hasUrlShortLink,
    getPageValidCfg: () => topchartCompaniesDownloadsValidCfg,
    getCustomValidUrlParams: getTopCompaniesCustomValidUrlParams,
  });

  useEffect(() => {
    buriedPointPage(pageLogInfo);
    // 进入页面时判断是否有tokenQuery等，有则进入
    initShortLinkCfg();
  }, []);

  const defaultFilterValue = useMemo(
    () => ({
      ...COMPANIES_DEFAULT_FILTER_VALUE,
    }),
    [],
  );

  // 筛选器相关配置如筛选器展示顺序，默认值等
  const filterCfg: TopchartFilterCfgType = useMemo(
    () => ({
      filterKeyList: [
        filterKeyMap.MY_FILTER,
        filterKeyMap.RANGE_TIME,
        filterKeyMap.REGION,
        filterKeyMap.DATASOURCESIM,
        filterKeyMap.GENRETOR,
        filterKeyMap.IPO_STATUS,
        filterKeyMap.COUNTRY,
      ],
      defaultValue: defaultFilterValue,
      resetFilterValue: {
        ...defaultFilterValue,
        [filterKeyMap.RANGE_TIME]: {
          type: 'month',
          value: [moment().subtract(6, 'months'), moment().subtract(1, 'months')],
        },
      },
      specialParams: {
        regionOptions, // 国家筛选器options,
        defalutMarket: ['global'], // 没取到交集用的默认值
        disableNoChina: true,
        isTopCompanies: true,
        showSelectType: true,
        rangeTimeType: FilterTimeType.dayWeekMonthYearRange,
        disableIegg: true,
        disableAnnie: false,
        typeList: ['month', 'year'], // ,'date', 'week',
        [filterKeyMap.COUNTRY]: {
          value: headQuatersOptionsData?.map((item) => item?.value) || [],
          options: headQuatersOptionsData,
        },
      },
      showConfirmButton: true,
      haveFilterSaveBtn: true,
      handleFilterToApiParams: handleCompiniesFilterParamsToApi,
      hasUrlShortLink,
    }),
    [hasUrlShortLink, defaultFilterValue, headQuatersOptionsData, regionOptions],
  );

  return (
    <>
      <MarqueeBanner />
      <CompaniesCore
        topChartsType="top_companies_downloads"
      pageLogInfo={pageLogInfo}
      // reset需要的每个粒度默认有数据的时间
      rangeTimeComponentDefault={latestTimeTypeHasData}
      headQuatersOptionsData={headQuatersOptionsData}
      filterLoading={loading || shortLinkLoading}
      // 筛选器相关配置如筛选器展示顺序，默认值等
      filterCfg={filterCfg}
      // 从短链接中拿到校正后的url参数（与页面相关）
      urlCustomParams={urlCustomParams}
      // 筛选器有效的页面配置值
      shortLinkValidQuery={shortLinkValidQuery}
      // 从短链接拿到的筛选器分享值
      shortLinkFilterValue={shortLinkFilterValue}
    />
    </>
  );
}
